/**
 * Regulamento (specs 017 e 042): o que cada compra aceitou, as caixas do checkout e as versoes publicadas no painel.
 * Nenhuma peca decide nada: versao, documentos, horas e frases vem do backend. O `href` de cada documento chega pronto
 * (o app junta o endereco da API do ambiente), e o arquivo de uma versao nunca muda: abrir hoje e abrir daqui a um ano
 * mostram o mesmo byte.
 */
import type { ReactNode } from "react";
import { formatarBytes, formatarDataHora } from "../formatos";
import { IconeArquivo, IconeExterno } from "../icones";
import { Aviso, Carregando, Selo } from "./Basicos";
import { CampoCaixa } from "./Campo";

/** "Versão 3" ou "Versão 3, campanha de outubro". O numero e automatico; o rotulo e livre e opcional (017, 9.1). */
export const nomeDaVersao = (numero: number, rotulo?: string | null) => `Versão ${numero}${rotulo ? `, ${rotulo}` : ""}`;

/** O comeco do `sha256`, pra conferir de olho que dois arquivos sao o mesmo byte. */
export const shaCurto = (sha256: string) => sha256.slice(0, 12);

/** Um documento numa lista: o titulo abre o arquivo em outra aba; embaixo, o que o lugar quiser dizer dele. */
function LinhaDoDocumento({ titulo, href, selo, meta }: { titulo: string; href: string; selo?: ReactNode; meta?: ReactNode }) {
  return (
    <li className="mtc-regulamento__doc">
      <a className="mtc-regulamento__abrir" href={href} target="_blank" rel="noreferrer" aria-label={`Abrir ${titulo} em outra aba`}>
        <IconeArquivo tamanho={20} />
        <span className="mtc-regulamento__titulo">{titulo}</span>
        <span className="mtc-regulamento__acao">Abrir<IconeExterno tamanho={14} /></span>
      </a>
      {(selo || meta) && (
        <div className="mtc-regulamento__meta">
          {selo}
          {meta && <span>{meta}</span>}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------- o regulamento de uma compra

/** Como o aceite chegou (spec 017, decisao 11; ADMIN, spec 046): so o painel mostra. */
const ORIGEM_DO_ACEITE: Record<string, string> = { CHECKOUT: "Aceito no checkout", AREA: "Aceito pela área", IMPORTACAO: "Trazido do sistema antigo", ADMIN: "Registrado pelo administrador" };
/**
 * O registro do administrador (spec 046, 2.5) nao e clique do cliente: a hora dele e "Registrado em", nunca "Aceito em",
 * na area e no painel.
 */
const quandoFoi = (origem: string, quando: string) => `${origem === "ADMIN" ? "Registrado em" : "Aceito em"} ${formatarDataHora(quando)}`;

export type DocumentoAceito = { chave: string; titulo: string; sha256: string; aceito_em: string; origem: string; observacao?: string | null; href: string };
/** O que `GET /v1/{me,admin}/matriculas/:id/regulamento` devolve, com o `href` de cada documento ja montado. */
export type RegulamentoDeUmaCompra = { versao: { numero: number; rotulo: string | null; publicada_em: string } | null; documentos: DocumentoAceito[]; sem_aceite: string | null };

/**
 * "Regulamento desta compra" (spec 042, 3.6): a versao que esta compra aceitou, a hora do aceite (fuso de Sao Paulo) e
 * um link por documento. Cada plano mostra o seu: quem comprou tres vezes ve tres datas. Sem aceite gravado, a frase do
 * backend. `detalhado` (painel): o `sha256` curto, como o aceite chegou e a observacao. `acaoSemAceite` (painel, spec
 * 046): o lugar do "Registrar regulamento", que so aparece quando a compra nao tem aceite; o aceite que existe nao se
 * reescreve.
 */
export function RegulamentoDaCompra({ regulamento, carregando, erro, detalhado, acaoSemAceite, titulo = "Regulamento desta compra" }: { regulamento?: RegulamentoDeUmaCompra | null; carregando?: boolean; erro?: string | null; detalhado?: boolean; acaoSemAceite?: ReactNode; /** Nulo quando o lugar ja tem o titulo (a secao do painel). */ titulo?: string | null }) {
  const docs = regulamento?.documentos ?? [];
  // Um clique aceita todos os documentos da compra: a hora e a mesma, e fica uma vez so. Se um dia vierem horas
  // diferentes (aceite corrigido depois), cada documento mostra a sua.
  const horas = new Set(docs.map((d) => d.aceito_em));
  const umaHora = horas.size === 1 ? docs[0]! : null;
  // A observacao (no painel) tambem costuma ser uma so pra compra inteira, como o motivo de um registro do administrador
  // (spec 046): aparece uma vez, embaixo da versao, em vez de repetida em cada documento.
  const observacoes = new Set(docs.map((d) => d.observacao ?? ""));
  const umaObservacao = detalhado && observacoes.size === 1 && docs[0]?.observacao ? docs[0].observacao : null;
  return (
    <section className="mtc-regulamento" aria-label={titulo ?? "Regulamento desta compra"}>
      {titulo && <span className="mtc-rotulo">{titulo}</span>}
      {carregando ? (
        <Carregando compacto texto="Buscando o regulamento" />
      ) : erro ? (
        <Aviso tipo="erro">{erro}</Aviso>
      ) : !regulamento || !docs.length ? (
        <>
          <p className="mtc-regulamento__sem">{regulamento?.sem_aceite ?? "Esta compra não tem o aceite do regulamento registrado."}</p>
          {acaoSemAceite && <div className="mtc-regulamento__registrar">{acaoSemAceite}</div>}
        </>
      ) : (
        <>
          <div className="mtc-regulamento__cabeca">
            {regulamento.versao && <strong className="mtc-regulamento__versao">{nomeDaVersao(regulamento.versao.numero, regulamento.versao.rotulo)}</strong>}
            {umaHora && <span className="mtc-regulamento__quando" data-aceito-em>{quandoFoi(umaHora.origem, umaHora.aceito_em)}</span>}
            {umaObservacao && <p className="mtc-regulamento__nota">{umaObservacao}</p>}
          </div>
          <ul className="mtc-regulamento__docs">
            {docs.map((d) => {
              // Na area, so o titulo; no painel, como chegou, o `sha256` curto e a observacao, separados por ponto.
              const partes = [
                !umaHora ? quandoFoi(d.origem, d.aceito_em) : null,
                detalhado ? ORIGEM_DO_ACEITE[d.origem] ?? d.origem : null,
                detalhado ? <span key="sha" className="mtc-mono">sha256 {shaCurto(d.sha256)}</span> : null,
                detalhado && d.observacao && !umaObservacao ? d.observacao : null,
              ].filter(Boolean);
              return <LinhaDoDocumento key={d.chave} titulo={d.titulo} href={d.href} meta={partes.length ? partes.flatMap((x, i) => (i ? [". ", x] : [x])) : undefined} />;
            })}
          </ul>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- o aceite no checkout

export type DocumentoParaAceitar = { chave: string; titulo: string; href: string };

/**
 * Uma caixa por documento (spec 017, decisao 8; spec 042, 3.4): "Li e aceito o Regulamento Geral", "Li e aceito o
 * Regulamento - Planos Exame", cada titulo abrindo o arquivo daquela versao. Quem decide que todas precisam estar
 * marcadas e a pagina; o servico confere de novo.
 */
export function AceiteDosDocumentos({ documentos, marcados, aoMudar }: { documentos: DocumentoParaAceitar[]; marcados: string[]; aoMudar: (marcados: string[]) => void }) {
  return (
    <div className="mtc-aceite-docs" role="group" aria-label="Documentos para aceitar">
      {documentos.map((d) => (
        <CampoCaixa key={d.chave} marcado={marcados.includes(d.chave)} aoMudar={(v) => aoMudar(v ? [...marcados.filter((x) => x !== d.chave), d.chave] : marcados.filter((x) => x !== d.chave))}>
          Li e aceito o <a href={d.href} target="_blank" rel="noreferrer">{d.titulo}</a>.
        </CampoCaixa>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- as versoes no painel

export type DocumentoDaVersao = { chave: string; titulo: string; href: string; arquivo: { nome: string; tipo: string; tamanho: number; sha256: string }; herdado_do_grupo: number | null };
export type VersaoDeRegulamentos = { numero: number; rotulo: string | null; situacao: "ATUAL" | "DESCONTINUADO"; publicado_em: string; publicado_por: string; compras: number; documentos: DocumentoDaVersao[] };

const formatoDoArquivo = (tipo: string) => (tipo.includes("pdf") ? "PDF" : "Texto");
/** "publicado_por" do backend ("operador:<id>" ou "sistema") como gente le. */
export const quemPublicou = (por: string) => (por.startsWith("operador:") ? "um administrador" : por === "sistema" ? "o sistema" : por);

/**
 * Uma versao publicada (painel, Cadastros > Regulamentos): numero, rotulo, situacao, quando e por quem, quantas compras
 * ela julga e cada documento com o arquivo, o `sha256` curto e se entrou nesta versao ou veio de uma anterior (017,
 * decisao 14). Nada se edita; `acoes` e o lugar do "Apagar" quando o backend ainda deixa.
 */
export function CartaoDaVersao({ versao, acoes }: { versao: VersaoDeRegulamentos; acoes?: ReactNode }) {
  const atual = versao.situacao === "ATUAL";
  return (
    <article className="mtc-versao-regulamento" data-atual={atual || undefined} aria-label={nomeDaVersao(versao.numero, versao.rotulo)}>
      <header className="mtc-versao-regulamento__cabeca">
        <div className="mtc-versao-regulamento__textos">
          <strong className="mtc-versao-regulamento__titulo">{nomeDaVersao(versao.numero, versao.rotulo)}</strong>
          <span className="mtc-versao-regulamento__meta">
            Publicada em {formatarDataHora(versao.publicado_em)} por {quemPublicou(versao.publicado_por)}. {versao.compras === 0 ? "Nenhuma compra com ela ainda." : versao.compras === 1 ? "1 compra com ela." : `${versao.compras.toLocaleString("pt-BR")} compras com ela.`}
          </span>
        </div>
        <Selo tom={atual ? "sucesso" : "neutro"}>{atual ? "Atual" : "Descontinuada"}</Selo>
      </header>
      <ul className="mtc-regulamento__docs">
        {versao.documentos.map((d) => (
          <LinhaDoDocumento
            key={d.chave}
            titulo={d.titulo}
            href={d.href}
            selo={d.herdado_do_grupo ? <Selo>Da versão {d.herdado_do_grupo}</Selo> : <Selo tom="acento">Novo nesta versão</Selo>}
            meta={<>{d.arquivo.nome}, {formatoDoArquivo(d.arquivo.tipo)}, {formatarBytes(d.arquivo.tamanho)}. <span className="mtc-mono">sha256 {shaCurto(d.arquivo.sha256)}</span></>}
          />
        ))}
      </ul>
      {acoes && <div className="mtc-versao-regulamento__acoes">{acoes}</div>}
    </article>
  );
}
